import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Nav from "../Components/Nav";
import { getAuth } from "firebase/auth";
import { db } from "../config";
import { doc, getDoc, updateDoc, addDoc, collection } from "firebase/firestore";
import axios from "axios";

function Withdrawal() {
  const [walletAddress, setWalletAddress] = useState("");
  const [cryptoType, setCryptoType] = useState("BTC");
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState(null);
  const [balance, setBalance] = useState(0);
  const [withdrawalLimit, setWithdrawalLimit] = useState(1000);
  const [withdrawalFeePercentage, setWithdrawalFeePercentage] = useState(0);
  const [withdrawalFee, setWithdrawalFee] = useState(0);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const cryptoOptions = ["BTC", "ETH", "USDT", "TRON", "LTC"];
  const MIN_WITHDRAWAL = 10;
  const MAX_WITHDRAWALS = {
    BTC: 200000,
    ETH: 200000,
    USDT: 200000,
    TRON: 200000,
    LTC: 200000,
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setBalance(data.balance || 0);
            setUsername(userDoc.data().fullname ); // Use email if username is not set
            setWithdrawalLimit(data.withdrawalLimit ?? 1000);
            setWithdrawalFeePercentage(data.withdrawalFeePercentage ?? 0);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setMessage("Error fetching user data.");
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        navigate("/auth/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    if (!user) {
      setMessage("User not authenticated.");
      return;
    }
    if (!walletAddress || !amount) {
      setMessage("All fields are required.");
      return;
    }

    const withdrawalAmount = parseFloat(amount);
    if (isNaN(withdrawalAmount) || withdrawalAmount <= 0) {
      setMessage("Please enter a valid amount.");
      return;
    }
    if (withdrawalAmount < MIN_WITHDRAWAL) {
      setMessage(`Amount must be at least $${MIN_WITHDRAWAL}.`);
      return;
    }
    if (withdrawalAmount > balance) {
      setMessage("Insufficient balance.");
      return;
    }
    if (
      withdrawalAmount > Math.min(withdrawalLimit, MAX_WITHDRAWALS[cryptoType])
    ) {
      setMessage(
        `Amount exceeds the withdrawal limit of $${Math.min(
          withdrawalLimit,
          MAX_WITHDRAWALS[cryptoType]
        )}.`
      );
      return;
    }

    const fee = (withdrawalFeePercentage / 100) * withdrawalAmount;
    setWithdrawalFee(fee);

    if (withdrawalFeePercentage > 0) {
      setPendingWithdrawal({
        amount: withdrawalAmount,
        walletAddress,
        cryptoType,
      });
      setShowFeeModal(true);
    } else {
      confirmWithdrawal({
        amount: withdrawalAmount,
        walletAddress,
        cryptoType,
      });
    }
  };

  const confirmWithdrawal = async (data = pendingWithdrawal) => {
    setShowFeeModal(false);
    setSubmitting(true);

    if (!data || !data.amount || !data.walletAddress || !data.cryptoType) {
      setMessage("Withdrawal data is incomplete.");
      setSubmitting(false);
      return;
    }

    try {
      const amount = parseFloat(data.amount);
      const { walletAddress, cryptoType } = data;

      await addDoc(collection(db, "withdrawals"), {
        userId: user.uid,
        email: user.email,
        username: username,
        amount,
        fee: withdrawalFee,
        total: amount + withdrawalFee,
        walletAddress,
        cryptoType,
        status: "pending",
        createdAt: new Date(),
      });

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { balance: balance - amount });

      setBalance((prev) => prev - amount);
      setMessage("Withdrawal submitted successfully.");
      setAmount("");
      setWalletAddress("");
      setPendingWithdrawal(null);

      await axios.post("https://renderbackend-i8fx.onrender.com/send-mail", {
        email: user.email,
        subject: "Withdrawal Submitted",
        CompanyName: "TRUE LEDGER ASSIST",
        htmlToSend: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
            <div style="text-align: center; margin-bottom: 20px;">
              <img src="https://trueledgerassist.com/public/uploads/logo/light.png" alt="TRUE LEDGER ASSIST Logo" style="height: 60px;" />
              <h2 style="margin: 10px 0; color: #1a1a1a;">TRUE LEDGER ASSIST</h2>
            </div>
            <h3 style="color: #333;">Hi! INVESTOR ${username}</h3>
            <p style="color: #555;">We've received your withdrawal request:</p>
            <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Method:</strong></td>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;">${cryptoType}</td>
              </tr>
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Amount:</strong></td>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style={{ color: "#212529" }}>$${amount.toFixed(
                  2
                )}</span></td>
              </tr>
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Fee:</strong></td>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style={{ color: "#212529" }}>$${withdrawalFee.toFixed(
                  2
                )}</span></td>
              </tr>
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Status:</strong></td>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style={{ color: "orange" }}>Pending</span></td>
              </tr>
            </table>
            <p style="color: #555;">Your crypto will be sent to the wallet address below:</p>
            <p style="color: #111;"><strong>${cryptoType}:</strong> ${walletAddress}</p>
            <p style="margin-top: 30px; color: #555;">Thank you for using TRUE LEDGER ASSIST!</p>
            <p style="color: #888;">– The TRUE LEDGER ASSIST Team</p>
            <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
            <p style="font-size: 12px; color: #aaa; text-align: center;">
              © ${new Date().getFullYear()} TRUE LEDGER ASSIST. All rights reserved.
            </p>
          </div>
        `,
      });
    } catch (err) {
      console.error("Error during withdrawal:", err);
      setMessage("Error submitting withdrawal.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    if (walletAddress) {
      navigator.clipboard
        .writeText(walletAddress)
        .then(() => {
          setMessage("Wallet address copied!");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          setMessage("Failed to copy wallet address.");
        });
    } else {
      setMessage("No wallet address to copy.");
    }
  };

  // if (loading) {
  //   return (
  //     <div className="text-center">
  //       <i className="fas fa-spinner fa-spin fa-2x"></i> Loading...
  //     </div>
  //   );
  // }

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>TRUE LEDGER ASSIST | Withdrawal</title>
        <link
          rel="icon"
          href="https://trueledgerassist.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352"
          type="image/png"
        />
      </Helmet>

      <style>
        {`
          .custom-select {
            display: inline-block;
            max-width: 100%;
            height: calc(2.25rem + 2px);
            padding: .375rem 1.75rem .375rem .75rem;
            line-height: 1.5;
            color: #0b57e3;
            vertical-align: middle;
            background: #fff url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center;
            background-size: 8px 10px;
            border: 4px solid black;
            border-radius: .25rem;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          .custom-select:focus {
            border-color: #80bdff;
            outline: 0;
          }
          .custom-select:disabled {
            color: #868e96;
            background-color: #e9ecef;
          }
          .footer-block {
            min-height: 400px;
            padding: 50px 0;
            background: linear-gradient(to right, #212529, #131516), url(../img/project4.jpg);
          }
          .foot-header {
            font-family: "Montserrat", Sans-serif;
            font-weight: 600;
            font-size: 1rem;
            margin-bottom: 25px;
            color: #4A4A4A;
          }
          .foot {
            display: block;
            font-size: small;
            margin-bottom: 8px;
            font-family: "Montserrat", Sans-serif;
            color: #A1A39E !important;
          }
          .foot:hover {
            text-decoration: none !important;
            color: #FE413C !important;
          }
          .foot::before {
            content: "-";
            margin-right: 3px;
            color: #FE413C;
          }
          .footer-credit {
            font-family: "Montserrat", Sans-serif;
            color: #FE413C;
            font-size: small;
          }
          .footer-block .small-image {
            width: 250px;
            height: auto;
          }
          .wallet-address {
            word-break: break-all;
            margin-top: 10px;
            font-family: monospace;
            color: #212529; /* text-dark for readability */
          }
          .message {
            margin-top: 15px;
            padding: 10px;
            border-radius: 5px;
          }
          .message.success {
            background-color: #d4edda;
            color: #155724;
          }
          .message.error {
            background-color: #f8d7da;
            color: #721c24;
          }
          .modal-backdrop {
            position: fixed;
            top: 0;
            left: 0;
            z-index: 1040;
            width: 100vw;
            height: 100vh;
            background-color: rgba(0, 0, 0, 0.5);
          }
          .modal {
            z-index: 1050;
            display: none;
            overflow: hidden;
            overflow-y: auto;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }
          .modal.show {
            display: block;
          }
          .modal-dialog {
            max-width: 500px;
            margin: 1.75rem auto;
            pointer-events: none;
            position: relative;
            top: 50%;
            transform: translateY(-50%);
          }
          .modal.fade .modal-dialog {
            transition: transform 0.3s ease-out;
            transform: translateY(-100%);
          }
          .modal.show .modal-dialog {
            transform: translateY(-50%);
          }
          .modal-content {
            background-color: #fff;
            border: 1px solid rgba(0, 0, 0, 0.2);
            border-radius: 0.3rem;
            pointer-events: auto;
          }
        `}
      </style>

      <div className="wrapper">
        <Nav />
        <div className="main-panel bg-light">
          <div className="content bg-light">
            <div className="page-inner">
              <div className="mt-2 mb-4">
                <h1 className="title1 text-dark">Request for Withdrawal</h1>
                <p className="text-dark">
                  Available Balance:{" "}
                  <span style={{ color: "#212529" }}>
                    ${balance.toFixed(2)}
                  </span>
                </p>
              </div>
              <div className="row">
                <div className="col-md-8">
                  <div className="card bg-light">
                    <div className="card-body">
                      <form onSubmit={handleWithdrawal}>
                        <div className="row">
                          <div className="mb-4 col-md-12">
                            <h5 className="card-title text-dark">
                              Select Cryptocurrency
                            </h5>
                            <select
                              className="custom-select"
                              value={cryptoType}
                              onChange={(e) => setCryptoType(e.target.value)}
                              required
                            >
                              {cryptoOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="mb-4 col-md-12">
                            <h5 className="card-title text-dark">
                              Wallet Address
                            </h5>
                            <input
                              className="form-control text-dark bg-light"
                              placeholder="Enter your wallet address"
                              type="text"
                              value={walletAddress}
                              onChange={(e) => setWalletAddress(e.target.value)}
                              required
                            />
                          </div>
                          {/* {walletAddress && (
                            <div className="col-md-12">
                              <h5 className="card-title text-dark">{cryptoType} Wallet Address</h5>
                              <p className="wallet-address">{walletAddress}</p>
                              <button
                                type="button"
                                className="btn btn-outline-secondary mt-2"
                                onClick={copyToClipboard}
                              >
                                Copy Address
                              </button>
                            </div>
                          )} */}
                          <div className="mb-4 col-md-12">
                            <h5 className="card-title text-dark">Amount ($)</h5>
                            <input
                              className="form-control text-dark bg-light"
                              placeholder="Enter amount"
                              type="number"
                              value={amount}
                              onChange={(e) => setAmount(e.target.value)}
                              required
                              min={MIN_WITHDRAWAL}
                              max={Math.min(
                                withdrawalLimit,
                                MAX_WITHDRAWALS[cryptoType]
                              )}
                              step="0.01"
                            />
                            <small className="text-muted">
                              Min: ${MIN_WITHDRAWAL} | Max: $
                              {Math.min(
                                withdrawalLimit,
                                MAX_WITHDRAWALS[cryptoType]
                              )}{" "}
                              | Fee: {withdrawalFeePercentage}%
                            </small>
                          </div>
                          {message && (
                            <div
                              className={`col-md-12 message ${
                                message.includes("success")
                                  ? "success"
                                  : "error"
                              }`}
                            >
                              {message}
                            </div>
                          )}
                          <div className="mt-4 col-md-12">
                            <button
                              type="submit"
                              className="px-5 btn btn-primary btn-lg"
                              disabled={submitting}
                            >
                              {submitting
                                ? "Processing..."
                                : "Request Withdrawal"}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="footer-block">
              <div className="container">
                <div className="row">
                  <div className="col-6 col-md-4 mb-3 mb-md-0">
                    <div className="foot-header">Company</div>
                    <div className="foot-body">
                      <a href="/dashboards/buy-plan" className="foot">
                        Investment Plans
                      </a>
                      <a href="/about.html" className="foot">
                        About Us
                      </a>
                      <a
                        href="https://www.google.com/maps?ll=-27.382143,152.993196&z=9&t=m&hl=en&gl=NG&mapclient=embed&q=Brisbane+Queensland+Australia"
                        className="foot"
                      >
                        <b>Australia Address:</b> Brisbane
                      </a>
                      <a href="/contact.html" className="foot">
                        Contact us
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Platforms</div>
                    <div className="foot-body">
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Platforms
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        MT4 for Windows
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        MT4 for iOS
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        MT4 for Android
                      </a>
                      <a
                        href="https://mine.mql5.com/mine?servers=-Demo%2CprimeMiningNetwork-Live&mine_server=primeMiningNetwork-Demo&demo_server=primeMiningNetwork-Demo&startup_mode=open_demo&lang=en"
                        className="foot"
                      >
                        Web miner
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Trading</div>
                    <div className="foot-body">
                      <a
                        href="https://trueledgerassist.com/auth/register"
                        className="foot"
                      >
                        Open Live Account
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Instruments
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Leaderboard
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Market Analysis
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Funding</div>
                    <div className="foot-body">
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Funding Methods
                      </a>
                      <a
                        href="https://trueledgerassist.com/auth/login"
                        className="foot"
                      >
                        Clients Protection
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Legal</div>
                    <div className="foot-body">
                      <a href="#" className="foot">
                        AML Policy
                      </a>
                      <a href="#" className="foot">
                        Cookie Policy
                      </a>
                      <a href="#" className="foot">
                        Privacy Policy
                      </a>
                      <a href="/faq.html" className="foot">
                        Risk Disclosure
                      </a>
                      <a href="/terms.html" className="foot">
                        Terms & Conditions
                      </a>
                    </div>
                  </div>
                </div>
                <div className="text-center mt-5">
                  <img
                    src="/home/logo-payments.png"
                    className="small-image"
                    alt="Payment Methods"
                  />
                </div>
                <div className="text-center mt-4">
                  <p className="footer-credit">
                    ©2024, TRUE LEDGER ASSIST - ALL RIGHTS RESERVED.
                  </p>
                </div>
              </div>
            </div>
            <div
              style={{
                position: "fixed",
                left: 0,
                bottom: 0,
                zIndex: 1,
                width: "100%",
              }}
            >
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#FFFFFF",
                  overflow: "hidden",
                  boxSizing: "content-box",
                  textAlign: "right",
                  lineHeight: "14px",
                  fontSize: "12px",
                  fontFeatureSettings: "normal",
                  textSizeAdjust: "100%",
                  padding: "0px",
                  margin: "0px",
                }}
              >
                <div style={{ height: "40px" }}>
                  <iframe
                    src="https://widget.coinlib.io/widget?type=horizontal_v2&theme=light&pref_coin_id=1505&invert_hover="
                    width="100%"
                    height={36}
                    scrolling="auto"
                    marginWidth={0}
                    marginHeight={0}
                    frameBorder={0}
                    border={0}
                    style={{ border: 0, margin: 0, padding: 0 }}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showFeeModal && (
        <>
          <div
            className="modal fade show d-block"
            id="feeModal"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="feeModalLabel"
            aria-modal="true"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="feeModalLabel">
                    Confirm Withdrawal Fee
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowFeeModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    A fee of{" "}
                    <span style={{ color: "#212529" }}>
                      ${withdrawalFee.toFixed(2)}
                    </span>{" "}
                    ({withdrawalFeePercentage}%) will be applied to your
                    withdrawal of{" "}
                    <span style={{ color: "#212529" }}>
                      ${pendingWithdrawal?.amount?.toFixed(2)}
                    </span>
                    .
                  </p>
                  <p>
                    Total deduction:{" "}
                    <span style={{ color: "#212529" }}>
                      ${(pendingWithdrawal?.amount + withdrawalFee).toFixed(2)}
                    </span>
                  </p>
                  <p>Proceed with the withdrawal?</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowFeeModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmWithdrawal}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowFeeModal(false)}
          ></div>
        </>
      )}
    </div>
  );
}

export default Withdrawal;

// import React, { useState, useEffect } from "react";
// import { Helmet } from "react-helmet";
// import { useNavigate } from "react-router-dom";
// import Nav from "../Components/Nav";
// import { getAuth } from "firebase/auth";
// import { db } from "../config";
// import { doc, getDoc, updateDoc, addDoc, collection } from "firebase/firestore";
// import axios from "axios";
// import { QRCodeCanvas } from "qrcode.react";

// function Withdrawal() {
//   const [walletAddress, setWalletAddress] = useState("");
//   const [cryptoType, setCryptoType] = useState("BTC");
//   const [amount, setAmount] = useState("");
//   const [message, setMessage] = useState("");
//   const [user, setUser] = useState(null);
//   const [balance, setBalance] = useState(0);
//   const [withdrawalLimit, setWithdrawalLimit] = useState(1000);
//   const [withdrawalFeePercentage, setWithdrawalFeePercentage] = useState(0);
//   const [withdrawalFee, setWithdrawalFee] = useState(0);
//   const [showFeeModal, setShowFeeModal] = useState(false);
//   const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [submitting, setSubmitting] = useState(false);
//   const navigate = useNavigate();

//   const cryptoOptions = ["BTC", "ETH", "USDT", "TRON", "LTC"];
//   const MIN_WITHDRAWAL = 50;
//   const MAX_WITHDRAWALS = {
//     BTC: 10000,
//     ETH: 200000,
//     USDT: 100000,
//     TRON: 200000,
//     LTC: 200000,
//   };

//   useEffect(() => {
//     const auth = getAuth();
//     const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
//       if (firebaseUser) {
//         setUser(firebaseUser);
//         try {
//           const userDocRef = doc(db, "users", firebaseUser.uid);
//           const userDoc = await getDoc(userDocRef);
//           if (userDoc.exists()) {
//             const data = userDoc.data();
//             setBalance(data.balance || 0);
//             setWithdrawalLimit(data.withdrawalLimit ?? 1000);
//             setWithdrawalFeePercentage(data.withdrawalFeePercentage ?? 0);
//           }
//         } catch (error) {
//           console.error("Error fetching user data:", error);
//           setMessage("Error fetching user data.");
//         } finally {
//           setLoading(false);
//         }
//       } else {
//         setUser(null);
//         navigate("/auth/login");
//       }
//     });
//     return () => unsubscribe();
//   }, [navigate]);

//   const handleWithdrawal = async (e) => {
//     e.preventDefault();
//     if (!user) {
//       setMessage("User not authenticated.");
//       return;
//     }
//     if (!walletAddress || !amount ) {
//       setMessage("All fields are required.");
//       return;
//     }

//     const withdrawalAmount = parseFloat(amount);
//     if (isNaN(withdrawalAmount) || withdrawalAmount <= 0) {
//       setMessage("Please enter a valid amount.");
//       return;
//     }
//     if (withdrawalAmount < MIN_WITHDRAWAL) {
//       setMessage(`Amount must be at least $${MIN_WITHDRAWAL}.`);
//       return;
//     }
//     if (withdrawalAmount > balance) {
//       setMessage("Insufficient balance.");
//       return;
//     }
//     if (withdrawalAmount > Math.min(withdrawalLimit, MAX_WITHDRAWALS[cryptoType])) {
//       setMessage(`Amount exceeds the withdrawal limit of $${Math.min(withdrawalLimit, MAX_WITHDRAWALS[cryptoType])}.`);
//       return;
//     }

//     const fee = (withdrawalFeePercentage / 100) * withdrawalAmount;
//     setWithdrawalFee(fee);

//     if (withdrawalFeePercentage > 0) {
//       setPendingWithdrawal({
//         amount: withdrawalAmount,
//         walletAddress,
//         cryptoType,
//       });
//       setShowFeeModal(true);
//     } else {
//       confirmWithdrawal({
//         amount: withdrawalAmount,
//         walletAddress,
//         cryptoType,
//       });
//     }
//   };

//   const confirmWithdrawal = async (data = pendingWithdrawal) => {
//     setShowFeeModal(false);
//     setSubmitting(true);

//     if (!data || !data.amount || !data.walletAddress || !data.cryptoType || !data.pin) {
//       setMessage("Withdrawal data is incomplete.");
//       setSubmitting(false);
//       return;
//     }

//     try {
//       const amount = parseFloat(data.amount);
//       const { walletAddress, cryptoType } = data;

//       await addDoc(collection(db, "withdrawals"), {
//         userId: user.uid,
//         email: user.email,
//         amount,
//         fee: withdrawalFee,
//         total: amount + withdrawalFee,
//         walletAddress,
//         cryptoType,
//         status: "pending",
//         createdAt: new Date(),
//       });

//       const userRef = doc(db, "users", user.uid);
//       await updateDoc(userRef, { balance: balance - amount });

//       setBalance((prev) => prev - amount);
//       setMessage("Withdrawal submitted successfully.");
//       setAmount("");
//       setWalletAddress("");
//       setPendingWithdrawal(null);

//       await axios.post("https://renderbackend-i8fx.onrender.com/send-mail", {
//         email: user.email,
//         subject: "Withdrawal Submitted",
//         CompanyName: "TrueLedgerAssist",
//         htmlToSend: `
//           <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
//             <div style="text-align: center; margin-bottom: 20px;">
//               <img src="https://trueledgerassist.com/public/uploads/logo/light.png" alt="TrueLedgerAssist Logo" style="height: 60px;" />
//               <h2 style="margin: 10px 0; color: #1a1a1a;">TrueLedgerAssist</h2>
//             </div>
//             <h3 style="color: #333;">Hi!</h3>
//             <p style="color: #555;">We've received your withdrawal request:</p>
//             <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
//               <tr>
//                 <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Method:</strong></td>
//                 <td style="padding: 8px; border-bottom: 1px solid #ddd;">${cryptoType}</td>
//               </tr>
//               <tr>
//                 <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Amount:</strong></td>
//                 <td style="padding: 8px; border-bottom: 1px solid #ddd;">$${amount.toFixed(2)}</td>
//               </tr>
//               <tr>
//                 <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Fee:</strong></td>
//                 <td style="padding: 8px; border-bottom: 1px solid #ddd;">$${withdrawalFee.toFixed(2)}</td>
//               </tr>
//               <tr>
//                 <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Status:</strong></td>
//                 <td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style="color: orange; font-weight: bold;">Pending</span></td>
//               </tr>
//             </table>
//             <p style="color: #555;">Your crypto will be sent to the wallet address below:</p>
//             <p style="color: #111;"><strong>${cryptoType}:</strong> ${walletAddress}</p>
//             <p style="margin-top: 30px; color: #555;">Thank you for using TrueLedgerAssist!</p>
//             <p style="color: #888;">– The TrueLedgerAssist Team</p>
//             <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
//             <p style="font-size: 12px; color: #aaa; text-align: center;">
//               © ${new Date().getFullYear()} TrueLedgerAssist. All rights reserved.
//             </p>
//           </div>
//         `,
//       });
//     } catch (err) {
//       console.error("Error during withdrawal:", err);
//       setMessage("Error submitting withdrawal.");
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const copyToClipboard = () => {
//     if (walletAddress) {
//       navigator.clipboard.writeText(walletAddress).then(() => {
//         setMessage("Wallet address copied!");
//       }).catch((err) => {
//         console.error("Failed to copy: ", err);
//         setMessage("Failed to copy wallet address.");
//       });
//     } else {
//       setMessage("No wallet address to copy.");
//     }
//   };

//   return (
//     <div>
//       <style>
//         {`
//           .custom-select {
//             display: inline-block;
//             max-width: 100%;
//             height: calc(2.25rem + 2px);
//             padding: .375rem 1.75rem .375rem .75rem;
//             line-height: 1.5;
//             color: #0b57e3;
//             vertical-align: middle;
//             background: #fff url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center;
//             background-size: 8px 10px;
//             border: 4px solid black;
//             border-radius: .25rem;
//             -webkit-appearance: none;
//             -moz-appearance: none;
//             appearance: none;
//           }
//           .custom-select:focus {
//             border-color: #80bdff;
//             outline: 0;
//           }
//           .custom-select:disabled {
//             color: #868e96;
//             background-color: #e9ecef;
//           }
//           .footer-block {
//             min-height: 400px;
//             padding: 50px 0;
//             background: linear-gradient(to right, #212529, #131516), url(../img/project4.jpg);
//           }
//           .foot-header {
//             font-family: "Montserrat", Sans-serif;
//             font-weight: 600;
//             font-size: 1rem;
//             margin-bottom: 25px;
//             color: #4A4A4A;
//           }
//           .foot {
//             display: block;
//             font-size: small;
//             margin-bottom: 8px;
//             font-family: "Montserrat", Sans-serif;
//             color: #A1A39E !important;
//           }
//           .foot:hover {
//             text-decoration: none !important;
//             color: #FE413C !important;
//           }
//           .foot::before {
//             content: "-";
//             margin-right: 3px;
//             color: #FE413C;
//           }
//           .footer-credit {
//             font-family: "Montserrat", Sans-serif;
//             color: #FE413C;
//             font-size: small;
//           }
//           .footer-block .small-image {
//             width: 250px;
//             height: auto;
//           }
//           .qr-code-container {
//             margin-top: 20px;
//             text-align: center;
//           }
//           .wallet-address {
//             word-break: break-all;
//             margin-top: 10px;
//             font-family: monospace;
//           }
//           .message {
//             margin-top: 15px;
//             padding: 10px;
//             border-radius: 5px;
//           }
//           .message.success {
//             background-color: #d4edda;
//             color: #155724;
//           }
//           .message.error {
//             background-color: #f8d7da;
//             color: #721c24;
//           }
//         `}
//       </style>

//       <div className="wrapper">
//         <Nav />
//         <div className="main-panel bg-light">
//           <div className="content bg-light">
//             <div className="page-inner">
//               <div className="mt-2 mb-4">
//                 <h1 className="title1 text-dark">Request for Withdrawal</h1>
//                 <p className="text-dark">Available Balance: ${balance.toFixed(2)}</p>
//               </div>
//               <div className="row">
//                 <div className="col-md-8">
//                   <div className="card bg-light">
//                     <div className="card-body">
//                       <form onSubmit={handleWithdrawal}>
//                         <div className="row">
//                           <div className="mb-4 col-md-12">
//                             <h5 className="card-title text-dark">Select Cryptocurrency</h5>
//                             <select
//                               className="custom-select"
//                               value={cryptoType}
//                               onChange={(e) => setCryptoType(e.target.value)}
//                               required
//                             >
//                               {cryptoOptions.map((option) => (
//                                 <option key={option} value={option}>
//                                   {option}
//                                 </option>
//                               ))}
//                             </select>
//                           </div>
//                           <div className="mb-4 col-md-12">
//                             <h5 className="card-title text-dark">Wallet Address</h5>
//                             <input
//                               className="form-control text-dark bg-light"
//                               placeholder="Enter your wallet address"
//                               type="text"
//                               value={walletAddress}
//                               onChange={(e) => setWalletAddress(e.target.value)}
//                               required
//                             />
//                           </div>
//                           {walletAddress && (
//                             <div className="col-md-12 qr-code-container">
//                               <h5 className="card-title text-dark">{cryptoType} Wallet Address</h5>
//                               <QRCodeCanvas value={walletAddress} size={128} />
//                               <p className="wallet-address">{walletAddress}</p>
//                               <button
//                                 type="button"
//                                 className="btn btn-outline-secondary mt-2"
//                                 onClick={copyToClipboard}
//                               >
//                                 Copy Address
//                               </button>
//                             </div>
//                           )}
//                           <div className="mb-4 col-md-12">
//                             <h5 className="card-title text-dark">Amount ($)</h5>
//                             <input
//                               className="form-control text-dark bg-light"
//                               placeholder="Enter amount"
//                               type="number"
//                               value={amount}
//                               onChange={(e) => setAmount(e.target.value)}
//                               required
//                               min={MIN_WITHDRAWAL}
//                               max={Math.min(withdrawalLimit, MAX_WITHDRAWALS[cryptoType])}
//                               step="0.01"
//                             />
//                             <small className="text-muted">
//                               Min: ${MIN_WITHDRAWAL} | Max: ${Math.min(withdrawalLimit, MAX_WITHDRAWALS[cryptoType])} | Fee: {withdrawalFeePercentage}%
//                             </small>
//                           </div>

//                           {message && (
//                             <div className={`col-md-12 message ${message.includes("success") ? "success" : "error"}`}>
//                               {message}
//                             </div>
//                           )}
//                           <div className="mt-4 col-md-12">
//                             <button
//                               type="submit"
//                               className="px-5 btn btn-primary btn-lg"
//                               disabled={submitting}
//                             >
//                               {submitting ? "Processing..." : "Request Withdrawal"}
//                             </button>
//                           </div>
//                         </div>
//                       </form>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//             <div className="footer-block">
//               <div className="container">
//                 <div className="row">
//                   <div className="col-6 col-md-4 mb-3 mb-md-0">
//                     <div className="foot-header">Company</div>
//                     <div className="foot-body">
//                       <a href="/dashboards/buy-plan" className="foot">
//                         Investment Plans
//                       </a>
//                       <a href="/about.html" className="foot">About Us</a>
//                       <a
//                         href="https://www.google.com/maps?ll=-27.382143,152.993196&z=9&t=m&hl=en&gl=NG&mapclient=embed&q=Brisbane+Queensland+Australia"
//                         className="foot"
//                       >
//                         <b>Australia Address:</b> Brisbane
//                       </a>
//                       <a href="/contact.html" className="foot">Contact us</a>
//                     </div>
//                   </div>
//                   <div className="col-6 col-md-2 mb-3 mb-md-0">
//                     <div className="foot-header">Platforms</div>
//                     <div className="foot-body">
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">
//                         Platforms
//                       </a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">
//                         MT4 for Windows
//                       </a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">
//                         MT4 for iOS
//                       </a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">
//                         MT4 for Android
//                       </a>
//                       <a
//                         href="https://mine.mql5.com/mine?servers=-Demo%2CprimeMiningNetwork-Live&mine_server=primeMiningNetwork-Demo&demo_server=primeMiningNetwork-Demo&startup_mode=open_demo&lang=en"
//                         className="foot"
//                       >
//                         Web miner
//                       </a>
//                     </div>
//                   </div>
//                   <div className="col-6 col-md-2 mb-3 mb-md-0">
//                     <div className="foot-header">Trading</div>
//                     <div className="foot-body">
//                       <a href="https://trueledgerassist.com/auth/register" className="foot">
//                         Open Live Account
//                       </a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">
//                         Instruments
//                       </a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">
//                         Leaderboard
//                       </a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">
//                         Market Analysis
//                       </a>
//                     </div>
//                   </div>
//                   <div className="col-6 col-md-2 mb-3 mb-md-0">
//                     <div className="foot-header">Funding</div>
//                     <div className="foot-body">
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">
//                         Funding Methods
//                       </a>
//                       <a href="https://trueledgerassist.com/auth/login" className="foot">
//                         Clients Protection
//                       </a>
//                     </div>
//                   </div>
//                   <div className="col-6 col-md-2 mb-3 mb-md-0">
//                     <div className="foot-header">Legal</div>
//                     <div className="foot-body">
//                       <a href="#" className="foot">AML Policy</a>
//                       <a href="#" className="foot">Cookie Policy</a>
//                       <a href="#" className="foot">Privacy Policy</a>
//                       <a href="/faq.html" className="foot">Risk Disclosure</a>
//                       <a href="/terms.html" className="foot">Terms & Conditions</a>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="text-center mt-5">
//                   <img src="/home/logo-payments.png" className="small-image" alt="Payment Methods" />
//                 </div>
//                 <div className="text-center mt-4">
//                   <p className="footer-credit">©2024, TRUE LEDGER ASSIST - ALL RIGHTS RESERVED.</p>
//                 </div>
//                 {/* <div className="text-center mt-4">
//                   <div className="d-flex justify-content-center col-5 col-lg-3">
//                     <div>
//                       <img src="/home/award001.png" width="80%" alt="Award 1" />
//                     </div>
//                     <div>
//                       <img src="/home/award002.png" width="80%" alt="Award 2" />
//                     </div>
//                     <div>
//                       <img src="/home/award003.png" width="80%" alt="Award 3" />
//                     </div>
//                   </div>
//                 </div> */}
//               </div>
//             </div>
//             <div
//               style={{
//                 position: "fixed",
//                 left: 0,
//                 bottom: 0,
//                 zIndex: 1,
//                 width: "100%",
//               }}
//             >
//               <div
//                 style={{
//                   width: "100%",
//                   backgroundColor: "#FFFFFF",
//                   overflow: "hidden",
//                   boxSizing: "content-box",
//                   textAlign: "right",
//                   lineHeight: "14px",
//                   fontSize: "12px",
//                   fontFeatureSettings: "normal",
//                   textSizeAdjust: "100%",
//                   padding: "0px",
//                   margin: "0px",
//                 }}
//               >
//                 <div style={{ height: "40px" }}>
//                   <iframe
//                     src="https://widget.coinlib.io/widget?type=horizontal_v2&theme=light&pref_coin_id=1505&invert_hover="
//                     width="100%"
//                     height={36}
//                     scrolling="auto"
//                     marginWidth={0}
//                     marginHeight={0}
//                     frameBorder={0}
//                     border={0}
//                     style={{ border: 0, margin: 0, padding: 0 }}
//                   ></iframe>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       <div className="modal fade" id="feeModal" tabIndex="-1" aria-labelledby="feeModalLabel" aria-hidden="true">
//         <div className="modal-dialog">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title" id="feeModalLabel">Confirm Withdrawal Fee</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
//             </div>
//             <div className="modal-body">
//               <p>A fee of ${withdrawalFee.toFixed(2)} ({withdrawalFeePercentage}%) will be applied to your withdrawal of ${pendingWithdrawal?.amount?.toFixed(2)}.</p>
//               <p>Total deduction: ${(pendingWithdrawal?.amount + withdrawalFee).toFixed(2)}</p>
//               <p>Proceed with the withdrawal?</p>
//             </div>
//             <div className="modal-footer">
//               <button
//                 type="button"
//                 className="btn btn-secondary"
//                 data-bs-dismiss="modal"
//                 onClick={() => setShowFeeModal(false)}
//               >
//                 Cancel
//               </button>
//               <button type="button" className="btn btn-primary" onClick={confirmWithdrawal}>
//                 Confirm
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default Withdrawal;
